import { Routes } from '@/api/routes';
import {
  NUVEI_GOOGLE_PAY_EXTERNAL_TOKEN_PROVIDER,
  NUVEI_GOOGLE_PAY_GATEWAY,
  NUVEI_PAYMENT_SESSION_REFRESH_MS,
} from '@/constants/nuvei';
import { apiClient } from '@/services/apiClient';
import type { NuveiConfig, PaymentMethod } from '@/types/checkout';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { SafeChargeInstance, SafeChargeField, GetTokenResponse } from '@/types/nuvei';
import type { NuveiPaymentStatusResponse } from '@/types/payment';
import { logError } from '@/utils/logger';

export interface NuveiCardDetails {
  internalRequestId: number;
  status: string;
  errCode: number;
  merchantId: string;
  merchantSiteId: string;
  version: string;
  clientRequestId: string;
  sessionToken: string;
  brand: string;
  cardType: string;
  cardProduct: string;
  program: string;
  visaDirectSupport: string;
  issuerCountry: string;
  dccAllowed: boolean;
  isPrepaid: boolean;
  issuerBankName: string;
  mastercardSendSupport: string;
  isNotReloadableCard: boolean;
  bin: string;
  last4digits: string;
  ccExpMonth: string;
  ccExpYear: string;
}

interface BuildNuveiSaveCardPaymentRequestParams {
  email: string;
  paymentMethodId: string;
  sessionToken: string;
  cardholderName: string;
  cvc: string;
  pspIdentifier?: string;
}

interface BuildNuveiCardPaymentRequestParams {
  cardData: NuveiCardDetails;
  shouldSaveCard: boolean;
  paymentMethodId: string;
  cardholderName: string;
  email: string;
  sessionToken: string;
  pspIdentifier: string;
  transactionId?: string;
}

export const buildNuveiGenericPaymentPayload = ({
  paymentMethodSettings,
  params,
  sessionToken,
  email,
}: {
  paymentMethodSettings: PaymentMethod;
  params: Record<string, string>;
  sessionToken: string;
  email: string;
}) => {
  return {
    params: {
      sessionToken,
      ...getNuveiApmOptions(paymentMethodSettings, params),
    },
    paymentProvider: 'nuvei',
    receipt_email: params.email ? params.email : email,
    paymentMethodDisplayName: paymentMethodSettings.paymentMethodDisplayName,
    paymentMethodId: paymentMethodSettings.id,
  };
};

// Nuvei needs the shopper's identity on `billingAddress` and `userDetails` to run the Touch 'n Go
// redirect. The values reach us as the `mandatoryUserFields` the routed method asked the shopper
// to fill. Each APM's extras travel under their own key, matching the wallet payload, and the
// block is sent only when complete because Nuvei rejects a partial one.
const TOUCHNGO_SHOPPER_FIELDS = ['firstName', 'lastName', 'phone'] as const;

const getNuveiApmOptions = (
  paymentMethodSettings: PaymentMethod,
  params: Record<string, string>
): Record<string, Record<string, string>> => {
  const pspMethodName = paymentMethodSettings.pspPaymentMethodName || paymentMethodSettings.paymentMethodName;
  if (pspMethodName !== PaymentMethodEnum.TOUCHNGO) {
    return {};
  }

  const touchnGo = TOUCHNGO_SHOPPER_FIELDS.reduce<Record<string, string>>((details, field) => {
    const value = params[field]?.trim();
    return value ? { ...details, [field]: value } : details;
  }, {});

  return Object.keys(touchnGo).length === TOUCHNGO_SHOPPER_FIELDS.length ? { touchnGo } : {};
};

export const buildNuveiSaveCardPaymentRequest = (params: BuildNuveiSaveCardPaymentRequestParams) => ({
  paymentProvider: ProviderEnum.NUVEI,
  receipt_email: params.email,
  params: {
    sessionToken: params.sessionToken,
    pspIdentifier: params.pspIdentifier,
    paymentMethodToken: params.pspIdentifier,
    type: 'userPaymentOptions',
    cvv: params.cvc,
  },
  paymentMethodDisplayName: 'Credit Card',
  paymentMethodId: params.paymentMethodId,
});

/**
 * Get Nuvei card token
 */
export const getNuveiCardToken = async (
  safeChargeInstance: SafeChargeInstance,
  cardField: SafeChargeField,
  sessionToken: string,
  cardholderName: string,
  email: string,
  country: string
): Promise<GetTokenResponse | null> => {
  try {
    const response = (await safeChargeInstance.getToken(cardField, {
      sessionToken,
      cardHolderName: cardholderName,
      billingAddress: {
        email,
        country,
      },
    })) as GetTokenResponse;

    if (response.status === 'SUCCESS') {
      return response;
    }
    return null;
  } catch (error) {
    logError('Error getting Nuvei card token', error);
    return null;
  }
};

export const getNuveiCardDetails = async (
  safeChargeInstance: SafeChargeInstance,
  sessionToken: string
): Promise<NuveiCardDetails | null> => {
  try {
    const cardDetails = await new Promise<NuveiCardDetails>((resolve, reject) => {
      safeChargeInstance.getCardDetails(
        {
          sessionToken,
          useFields: true,
        },
        (response: unknown) => {
          if (response && typeof response === 'object') {
            resolve(response as NuveiCardDetails);
          } else {
            reject(new Error('Invalid card details response'));
          }
        }
      );
    });
    return cardDetails;
  } catch (error) {
    logError('Error getting Nuvei card details', error);
    return null;
  }
};

export const buildNuveiCardPaymentRequest = (params: BuildNuveiCardPaymentRequestParams) => ({
  paymentProvider: 'nuvei',
  receipt_email: params.email,
  paymentMethodDisplayName: 'Credit Card',
  paymentMethodId: params.paymentMethodId,
  params: {
    sessionToken: params.sessionToken,
    pspIdentifier: params.pspIdentifier,
    paymentMethodToken: params.pspIdentifier,
    transactionId: params.transactionId,
    type: 'ccTempToken',
    shouldSaveCard: params.shouldSaveCard,
    cardDetails: {
      bin: params.cardData.bin,
      expirationMonth: params.cardData.ccExpMonth,
      expirationYear: params.cardData.ccExpYear,
      lastFourDigits: params.cardData.last4digits,
      nameOnCard: params.cardholderName ?? '',
      cardBrand: params.cardData.brand,
      cardType: '',
      countryOfIssuance: '',
    },
  },
});

export const getNuveiPaymentStatus = async (
  sessionToken: string,
  paymentMethodId: string
): Promise<NuveiPaymentStatusResponse | null> => {
  try {
    const response = await apiClient.post<NuveiPaymentStatusResponse>(Routes.PAYMENT_STATUS, {
      sessionToken,
      paymentMethodId,
    });
    return response;
  } catch (error) {
    logError('Error getting Nuvei payment status', error);
    return null;
  }
};

export const buildNuveiWalletPaymentRequest = ({
  paymentMethodId,
  paymentMethodDisplayName,
  email,
  sessionToken,
  walletParams,
}: {
  paymentMethodId: string;
  paymentMethodDisplayName: string;
  email: string;
  sessionToken: string;
  walletParams: { transactionId: string } | { applePay: { walletIssuedEncryptedToken: string } };
}) => ({
  paymentProvider: ProviderEnum.NUVEI,
  receipt_email: email,
  paymentMethodDisplayName,
  paymentMethodId,
  params: {
    sessionToken,
    ...walletParams,
  },
});

export interface NuveiGooglePayBillingAddress {
  name?: string;
  address1?: string;
  address2?: string;
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
  countryCode?: string;
  phoneNumber?: string;
}

export const buildNuveiGooglePayAllowedPaymentMethods = (gatewayMerchantId: string) => [
  {
    type: 'CARD',
    parameters: {
      allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
      allowedCardNetworks: ['AMEX', 'VISA', 'MASTERCARD', 'DISCOVER', 'JCB'],
    },
    tokenizationSpecification: {
      type: 'PAYMENT_GATEWAY',
      parameters: {
        gateway: NUVEI_GOOGLE_PAY_GATEWAY,
        gatewayMerchantId,
      },
    },
  },
];

export const isNuveiPaymentSessionFresh = (createdAt: number, now = Date.now()): boolean =>
  now - createdAt < NUVEI_PAYMENT_SESSION_REFRESH_MS;

const NUVEI_SESSION_REFRESH_MAX_ATTEMPTS = 3;

export const prefetchNuveiSessionForCurrentBinding = async <TSession extends { bindingKey: string }>(
  prefetchSession: () => Promise<TSession | null>,
  getCurrentBindingKey: () => string,
  clearPrefetchedSession: () => void
): Promise<TSession | null> => {
  for (let attempt = 0; attempt < NUVEI_SESSION_REFRESH_MAX_ATTEMPTS; attempt += 1) {
    const prefetched = await prefetchSession();
    if (!prefetched) {
      return null;
    }
    if (prefetched.bindingKey === getCurrentBindingKey()) {
      return prefetched;
    }
    clearPrefetchedSession();
  }
  return null;
};

export const mapNuveiGooglePayBillingAddress = (
  address: NuveiGooglePayBillingAddress | undefined,
  fallback: { email: string; country: string }
) => {
  if (!address) return fallback;

  const name = address.name?.trim() || '';
  const spaceIdx = name.indexOf(' ');
  return {
    email: fallback.email,
    firstName: spaceIdx > 0 ? name.slice(0, spaceIdx) : name || undefined,
    lastName: spaceIdx > 0 ? name.slice(spaceIdx + 1) : undefined,
    country: address.countryCode || fallback.country,
    address: address.address1,
    addressLine2: address.address2,
    zip: address.postalCode,
    city: address.locality,
    state: address.administrativeArea,
    phone: address.phoneNumber,
  };
};

export const buildNuveiGooglePayCreatePaymentRequest = ({
  sessionToken,
  merchantId,
  merchantSiteId,
  clientUniqueId,
  amount,
  currency,
  googlePay3Dflow,
  billingAddress,
  email,
  country,
  mobileToken,
}: {
  sessionToken: string;
  merchantId: string;
  merchantSiteId: string;
  clientUniqueId: string;
  amount: string;
  currency: string;
  googlePay3Dflow: 'enable' | 'disable';
  billingAddress: ReturnType<typeof mapNuveiGooglePayBillingAddress>;
  email: string;
  country: string;
  mobileToken: string;
}) => ({
  sessionToken,
  merchantId,
  merchantSiteId,
  clientUniqueId,
  amount,
  currency,
  googlePay3Dflow,
  billingAddress,
  userDetails: {
    email,
    country,
  },
  paymentOption: {
    card: {
      externalToken: {
        externalTokenProvider: NUVEI_GOOGLE_PAY_EXTERNAL_TOKEN_PROVIDER,
        mobileToken,
      },
    },
  },
});

const scriptLoadPromises = new Map<string, Promise<void>>();

export const loadScriptOnce = (src: string, id: string): Promise<void> => {
  const inFlight = scriptLoadPromises.get(id);
  if (inFlight) {
    return inFlight;
  }

  const promise = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Document unavailable'));
      return;
    }
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing?.dataset.loaded === 'true') {
      resolve();
      return;
    }
    // Prior failed / in-progress DOM node without dataset.loaded — remove and load fresh.
    existing?.remove();

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => {
      script.remove();
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });

  scriptLoadPromises.set(id, promise);
  void promise.catch(() => {
    if (scriptLoadPromises.get(id) === promise) {
      scriptLoadPromises.delete(id);
    }
  });
  return promise;
};

/**
 * Same pattern as Adyen Google Pay: wallet identifiers live on PSP `publicConfig`
 * (Mongo paymentProvider), delivered via payment-method `publicConfig` and/or `pks.nuvei`.
 * Method publicConfig wins when both are present.
 */
export const resolveNuveiWalletConfig = (
  paymentKeysNuvei: NuveiConfig | undefined,
  methodPublicConfig?: Record<string, unknown> | null
): NuveiConfig | undefined => {
  if (!paymentKeysNuvei && !methodPublicConfig) {
    return undefined;
  }
  const fromMethod = Object.fromEntries(
    Object.entries(methodPublicConfig || {}).filter(([, value]) => value != null)
  ) as Partial<NuveiConfig>;
  return {
    ...(paymentKeysNuvei || ({} as NuveiConfig)),
    ...fromMethod,
  };
};

/**
 * SafeCharge Apple Pay oncancel passes `{ status: "canceled" }` without `cancelled: true`.
 * Treat those as user/sheet abort so we do not show a hard payment error.
 */
export const isNuveiWalletPaymentCancelled = (sdkResult: {
  cancelled?: boolean;
  status?: string;
  result?: string;
}): boolean => {
  if (sdkResult.cancelled) {
    return true;
  }
  const status = (sdkResult.status || sdkResult.result || '').toLowerCase();
  return status === 'canceled' || status === 'cancelled' || status === 'abort';
};

export const resolveApplePaySessionVersion = (ApplePaySessionCtor: {
  supportsVersion?: (version: number) => boolean;
}): number => {
  // Match Adyen (`resolveSupportedVersion(14)`): pick the highest supported
  // version. SafeCharge prefers 3, but v3 alone is not required for Nuvei
  // merchant validation and older caps have caused currency/request rejects.
  for (let version = 14; version >= 1; version -= 1) {
    if (ApplePaySessionCtor.supportsVersion?.(version)) return version;
  }
  return 1;
};

/**
 * Requests merchant validation through Checkout Gateway so Nuvei receives the
 * verified top-level domain as Referer instead of the cross-origin iframe host.
 */
export const fetchNuveiAppleMerchantSession = async (
  paymentMethodId: string,
  validationURL: string,
  topLevelDomain: string
): Promise<Record<string, unknown>> => {
  return await apiClient.post<Record<string, unknown>>(Routes.NUVEI_APPLE_PAY_MERCHANT_SESSION, {
    paymentMethodId,
    validationUrl: validationURL,
    topLevelDomain,
  });
};
